import { AlertTriangle, CreditCard, HardDrive, Mail } from 'lucide-react'
import { Badge, Panel, PanelBody, PanelHeader, PanelTitle, Spinner } from '@bb/ui'
import { de, formatBytes, formatCount, plural } from '../../i18n'
import { useAdminStats } from './useStats'
import { MetricTile } from './MetricTile'
import type { AdminStats } from './types'

const SOURCE_TYPE_LABEL: Record<'email' | 'stripe', string> = {
  email: de.dashboard.dataSources.type.email,
  stripe: de.dashboard.dataSources.type.stripe,
}

export function DashboardPage() {
  const stats = useAdminStats()

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <h1 className="mb-5 text-xl font-semibold text-ink">{de.dashboard.title}</h1>

      {stats.isPending || !stats.data ? (
        <div className="flex justify-center py-24"><Spinner className="size-6" /></div>
      ) : (
        <Dashboard stats={stats.data} />
      )}
    </div>
  )
}

function Dashboard({ stats }: { stats: AdminStats }) {
  const failingTotal = stats.dataSources.reduce((sum, row) => sum + row.failing, 0)

  return (
    <div className="space-y-5">
      <Panel>
        <PanelHeader><PanelTitle>{de.dashboard.sections.accounts}</PanelTitle></PanelHeader>
        <div className="grid grid-cols-2 divide-x divide-y divide-rule sm:grid-cols-4">
          <MetricTile label={de.dashboard.accounts.total} value={formatCount(stats.accounts.total)} />
          <MetricTile label={de.dashboard.accounts.newLast30Days} value={formatCount(stats.accounts.newLast30Days)} />
          <MetricTile label={de.dashboard.accounts.activeLast30Days} value={formatCount(stats.accounts.activeLast30Days)} />
          <MetricTile label={de.dashboard.companies.total} value={formatCount(stats.companies.total)} />
        </div>
      </Panel>

      <Panel>
        <PanelHeader><PanelTitle>{de.dashboard.sections.volume}</PanelTitle></PanelHeader>
        <div className="grid grid-cols-1 divide-x divide-y divide-rule sm:grid-cols-3">
          <MetricTile label={de.dashboard.volume.transactions} value={formatCount(stats.volume.transactions)} />
          <MetricTile label={de.dashboard.volume.documents} value={formatCount(stats.volume.documents)} />
          <MetricTile label={de.dashboard.volume.invoices} value={formatCount(stats.volume.invoices)} />
        </div>
      </Panel>

      {/* §5: "die wichtigste Zeile" — a failing data source is a silent
          outage at a customer, so this panel gets a visible warning rather
          than blending in with the others. */}
      <Panel className={failingTotal > 0 ? 'border-danger' : undefined}>
        <PanelHeader>
          <PanelTitle>{de.dashboard.sections.dataSources}</PanelTitle>
          {failingTotal > 0 ? (
            <Badge tone="danger">
              <AlertTriangle className="size-3" />
              {formatCount(failingTotal)}
            </Badge>
          ) : null}
        </PanelHeader>
        {stats.dataSources.length === 0 ? (
          <PanelBody><p className="text-base text-ink-muted">{de.dashboard.dataSources.empty}</p></PanelBody>
        ) : (
          <ul className="divide-y divide-rule">
            {stats.dataSources.map((row) => {
              const Icon = row.type === 'stripe' ? CreditCard : Mail
              return (
              <li key={row.type} className="flex items-center gap-3 px-4 py-3">
                <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-sunken text-ink-muted">
                  <Icon className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-medium text-ink">{SOURCE_TYPE_LABEL[row.type]}</p>
                  <p className="text-xs text-ink-muted">
                    {formatCount(row.total)} {plural(row.total, de.dashboard.dataSources.source, de.dashboard.dataSources.sources)}
                  </p>
                </div>
                {row.failing > 0 ? (
                  <Badge tone="danger">{de.dashboard.dataSources.failing(row.failing)}</Badge>
                ) : (
                  <span className="text-xs text-ink-faint">{de.dashboard.dataSources.noneFailing}</span>
                )}
              </li>
              )
            })}
          </ul>
        )}
      </Panel>

      <div className="grid gap-5 sm:grid-cols-2">
        <Panel>
          <PanelHeader><PanelTitle>{de.dashboard.sections.managedMailboxes}</PanelTitle></PanelHeader>
          <div className="grid grid-cols-2 divide-x divide-rule">
            <MetricTile label={de.dashboard.managedMailboxes.provisioned} value={formatCount(stats.managedMailboxes.provisioned)} />
            <MetricTile label={de.dashboard.managedMailboxes.missing} value={formatCount(stats.managedMailboxes.missing)} tone={stats.managedMailboxes.missing > 0 ? 'danger' : 'neutral'} />
          </div>
        </Panel>

        <Panel>
          <PanelHeader><PanelTitle>{de.dashboard.sections.storage}</PanelTitle></PanelHeader>
          <div className="flex items-center gap-3 px-4 py-3">
            <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-sunken text-ink-muted">
              <HardDrive className="size-4" />
            </span>
            <MetricTile label={de.dashboard.storage.documentBytes} value={formatBytes(stats.storage.documentBytes)} />
          </div>
        </Panel>
      </div>
    </div>
  )
}
